import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { Link } from "react-router-dom";
import { formatNumber, percent } from "../../utils/format.ts";

export interface FunnelStage {
  label: string;
  value: number;
  to?: string;
}

/**
 * Stages as bars scaled to the first, darker the further down the funnel,
 * each with its conversion from the stage before. A stage with `to` opens
 * the list behind it.
 */
export default function FunnelBars({ stages }: { stages: FunnelStage[] }) {
  const top = stages[0]?.value ?? 0;
  return (
    <Stack spacing={0.5}>
      {stages.map((stage, i) => {
        const prev = i > 0 ? stages[i - 1] : null;
        const opacity = 0.35 + (0.65 * i) / Math.max(stages.length - 1, 1);
        return (
          <ButtonBase
            key={stage.label}
            component={stage.to ? Link : "div"}
            to={stage.to}
            disabled={!stage.to}
            sx={{
              display: "block",
              textAlign: "left",
              px: 1,
              py: 1,
              mx: -1,
              borderRadius: 1.5,
              "&:hover": { bgcolor: "action.hover" },
            }}
          >
            <Stack direction="row" sx={{ alignItems: "baseline", mb: 0.75 }}>
              <Typography variant="body1" sx={{ flex: 1 }}>
                {stage.label}
              </Typography>
              {prev && prev.value > 0 && (
                <Typography variant="body2" sx={{ color: "text.secondary", mr: 1.5 }}>
                  {percent(stage.value, prev.value)} of {prev.label.toLowerCase()}
                </Typography>
              )}
              <Typography
                variant="body1"
                sx={{ fontWeight: 600, fontVariantNumeric: "tabular-nums" }}
              >
                {formatNumber(stage.value)}
              </Typography>
            </Stack>
            <Box sx={{ height: 8, borderRadius: 4, bgcolor: "action.hover", overflow: "hidden" }}>
              <Box
                sx={{
                  height: "100%",
                  borderRadius: 4,
                  width: top > 0 ? `${Math.max((stage.value / top) * 100, stage.value ? 1 : 0)}%` : 0,
                  bgcolor: `rgba(var(--mui-palette-primary-mainChannel) / ${opacity})`,
                }}
              />
            </Box>
          </ButtonBase>
        );
      })}
    </Stack>
  );
}
